import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import CustomField from '@enview/interface/types/CustomFields';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getTeamsLinkedToBillValue,
  getTeamsWithAnyFieldValue,
  lookupCustomFieldValue,
} from '../../../helpers/BillHelper';
import { getSelectedTeamIds, getTeamMode } from '../../../dux';
import { useSelector } from 'react-redux';
import RichTextEditor from '../ActivityFeed/CommentForm/RichTextEditor';
import { Descendant } from 'slate';
import { ContainerNode, UserAction } from '@enview/interface/types/actions/UserAction';
import { CustomFieldsAPI } from '../../../api';
import './BillCustomField.scss';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import TeamSelector from '../TeamSelector';
import { CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT } from '../../../utils/constants';
import { Spinner } from 'react-bootstrap';
import { nodeHasText } from '../../../helpers/RichTextHelpers';
import * as BillAnalytics from '../../../analytics/BillAnalytics';
import { serializeToReactElement } from '@enview/interface/utils/RichTextAnnotation';
import _ from 'lodash-es';

interface BillCustomFieldValueProps {
  bill: BillSummary;
  field: CustomField;
  tooltipId?: string;
  onHover?: (content: React.ReactElement, anchorId: string) => void;
}

const CONFRIM_SAVE_TITLE = 'Select the teams you want to share with:';
const CONFRIM_DELETE_TITLE = 'Select the teams you want to delete from:';

export const BillCustomFieldValue: React.FC<BillCustomFieldValueProps> = ({
  bill,
  field,
  tooltipId,
  onHover,
}) => {
  const { title, orgId } = field;
  const teamModeId = useSelector(getTeamMode);
  const selectedTeamIds = useSelector(getSelectedTeamIds);
  const nodeRef = useRef<any>();

  // Look up current bill value for this field/workspace pair
  const billValue = useMemo(
    () => lookupCustomFieldValue(bill.customFields ?? {}, title, orgId, teamModeId),
    [bill.customFields, title, orgId, teamModeId],
  );

  // Derive initial comment from existing richText
  const getInitialCommentFromBillValue = (): Descendant[] => {
    const richText = billValue?.fieldValue?.richText as ContainerNode | undefined;

    if (richText && Array.isArray(richText.children)) {
      return richText.children as Descendant[];
    }
    return [];
  };

  const [comment, setComment] = useState<Descendant[]>(() =>
    getInitialCommentFromBillValue(),
  );

  const [displaySavePopup, setDisplaySavePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shouldArchive, setShouldArchive] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [upsertCustomFieldValue, { isLoading }] =
    CustomFieldsAPI.endpoints.upsertCustomFieldValue.useMutation();

  const nodeHasValue = useMemo(() => {
    return nodeHasText({ children: comment });
  }, [comment]);

  const onChangeToEditor = (event: Descendant[]) => {
    setComment(event);
  };

  const teamsWithBillFieldValue = useMemo(
    () =>
      getTeamsLinkedToBillValue(bill.customFields ?? {}, title, orgId, billValue?.id),
    [bill.customFields, title, orgId, billValue?.id],
  );

  const teamsWithAnyValue = useMemo(
    () => getTeamsWithAnyFieldValue(bill.customFields ?? {}, title, orgId),
    [bill.customFields, title, orgId],
  );

  const initialSelection = useMemo(() => {
    if (teamsWithBillFieldValue?.length > 0) return teamsWithBillFieldValue;
    if (selectedTeamIds?.length > 0) return selectedTeamIds;
    return [teamModeId];
  }, [teamsWithBillFieldValue, selectedTeamIds, teamModeId]);

  const hasValueSet = !!billValue?.fieldValue;

  const getUserAction = useMemo(() => {
    if (comment && comment.length) {
      return {
        commentJson: { children: comment },
      } as Partial<UserAction>;
    }

    if (billValue?.fieldValue?.richText) {
      return {
        commentJson: billValue.fieldValue.richText,
      } as Partial<UserAction>;
    }
  }, [comment, billValue?.fieldValue]);

  const getReadOnlyComment = useMemo(() => {
    if (billValue?.fieldValue?.richText) {
      return billValue.fieldValue.richText;
    }
    return { children: [] };
  }, [billValue?.fieldValue]);

  const onPostConfirm = useCallback(
    async (teams?: number[]) => {
      if (!orgId) return;

      const update = {
        fieldId: field.id,
        billId: bill.id,
        data: {
          teams: teams ? teams : selectedTeamIds,
          value: {
            richText: { children: comment },
          },
          orgId,
          shouldArchive,
          ...(hasValueSet && billValue?.id ? { valueId: billValue.id } : {}),
        },
      };

      try {
        if (!shouldArchive && !nodeHasValue) {
          // nothing to save, reset UI
          setComment(getInitialCommentFromBillValue());
          setIsEditMode(false);
          setDisplaySavePopup(false);
          setShouldArchive(false);
          return;
        }

        const { billFieldValue } = await upsertCustomFieldValue(update).unwrap();

        BillAnalytics.trackCustomFieldValueUpdated(
          bill.id,
          teams ? teams : selectedTeamIds,
          orgId,
          billFieldValue.customFieldId!,
          billFieldValue.id!,
        );
      } catch (err: any) {
        const errorMessage =
          err?.data?.message ?? 'Something went wrong while updating this field.';
        setStatusMessage(errorMessage);
        // TODO: Include this in app level alerts. Right now, errorMessage does nothing
      } finally {
        setComment(getInitialCommentFromBillValue());
        setIsEditMode(false);
        setDisplaySavePopup(false);
        setShouldArchive(false);
        setStatusMessage(null);
      }
    },
    [selectedTeamIds, comment, shouldArchive, hasValueSet, billValue?.id, nodeHasValue],
  );

  useEffect(() => {
    if (!isEditMode) return;

    const richText = billValue?.fieldValue?.richText as ContainerNode | undefined;
    const originalChildren: Descendant[] = Array.isArray(richText?.children)
      ? (richText?.children as Descendant[])
      : [];

    const resetState = () => {
      setComment(originalChildren);
      setIsEditMode(false);
      setDisplaySavePopup(false);
      setShouldArchive(false);
      setStatusMessage(null);
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (target.closest('.post-confirmation')) {
        return;
      }

      // So clicking close icon closes without saving
      if (target.closest('.close-icon')) {
        resetState();
        return;
      }

      // Click outside the editor
      if (nodeRef.current && !nodeRef.current.contains(target)) {
        const noChangeMade = _.isEqual(comment, originalChildren);

        if (noChangeMade) {
          resetState();
        } else {
          onPostConfirm([teamModeId]);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode, billValue, comment, teamModeId]);

  const onSaveClicked = () => {
    if (!comment && typeof comment !== 'object') return;
    setDisplaySavePopup(true);
  };

  const beginEdit = () => {
    setComment(getInitialCommentFromBillValue());
    setIsEditMode(true);
  };

  const saveButton = (
    <ConfirmActionPopover
      align="end"
      closePopup={() => setDisplaySavePopup(false)}
      content={
        <div className="post-confirmation">
          <p className="popover-confirm-body mt-1">
            {shouldArchive ? CONFRIM_DELETE_TITLE : CONFRIM_SAVE_TITLE}
          </p>
          <TeamSelector
            excludePersonalWorkspace={true}
            initialSelection={initialSelection}
            onSelectionChange={(teams) => {
              if (!orgId) return;
              // selected teams that already have a value and are not the current workspace
              const conflictingTeams = teams.filter(
                (teamId) =>
                  teamId !== teamModeId &&
                  teamsWithAnyValue.includes(teamId) &&
                  !teamsWithBillFieldValue.includes(teamId),
              );

              if (conflictingTeams.length > 0) {
                setStatusMessage(
                  'Some selected workspaces already have a value for this field. Saving will override those values.',
                );
              } else {
                setStatusMessage(null);
              }
            }}
            shouldDelete={shouldArchive}
            statusMessage={statusMessage}
          />
          <div className="text-center mt-3">
            <button
              className="btn btn-sm btn-block btn-action"
              onClick={() => onPostConfirm()}
              type="button"
            >
              Save
            </button>
          </div>
        </div>
      }
      positions={['left', 'top']}
      show={displaySavePopup}
      target={
        <div className="action-buttons">
          <button
            className="btn btn-link btn-sm cf-btn mr-2"
            disabled={!hasValueSet}
            onClick={() => {
              setShouldArchive(true);
              onSaveClicked();
            }}
            type="button"
          >
            Delete
          </button>

          <button
            className="btn btn-primary btn-sm cf-btn"
            disabled={!nodeHasValue}
            onClick={onSaveClicked}
            type="button"
          >
            Save
          </button>
        </div>
      }
      targetStyle={{ width: '100%' }}
      toggleCallback={() => {}}
    />
  );

  const tooltipContent = useMemo(
    () => serializeToReactElement(getReadOnlyComment),
    [getReadOnlyComment],
  );

  const handleMouseEnter = () => {
    if (onHover && tooltipId && nodeHasValue) {
      onHover(tooltipContent, tooltipId);
    }
  };

  if (isLoading) {
    return <Spinner animation="border" role="status"></Spinner>;
  } else if (!isEditMode) {
    return (
      <>
        <div
          className="cf-card-content"
          id={tooltipId}
          onClick={beginEdit}
          onMouseEnter={handleMouseEnter}
        >
          <span>{serializeToReactElement(getReadOnlyComment)}</span>
        </div>
      </>
    );
  } else {
    return (
      <div className="rich-text-editor" id="cf-rich-text" ref={nodeRef}>
        <RichTextEditor
          charLimit={CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT}
          onChangeToEditor={onChangeToEditor}
          placeholder={''}
          style={{ padding: '12px', maxHeight: '150px', overflowY: 'auto' }}
          userAction={getUserAction}
        />
        <div className="d-flex flex-row my-2 ml-auto">{saveButton}</div>
      </div>
    );
  }
};

export default BillCustomFieldValue;
